import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import {
  CRYPTO_NETWORKS,
  SETTING_GROUPS,
  SETTING_GROUP_LABELS,
  validateWalletAddress,
  type AdminSettingsResponse,
  type CryptoNetwork,
  type ResendDomainResult,
  type SettingGroup,
  type SettingView,
  type SettingsTestResult,
} from '@nexus/shared';

import { ApiError, apiFetch, apiGet, apiSend } from '../../lib/api';
import {
  Actions,
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  LoadErrorPage,
  LoadingPage,
  Modal,
  PageHeader,
  SelectField,
  TextField,
  When,
  centsEcho,
  centsToDollarInput,
  parseDollars,
  type Column,
} from '../../ui';

/**
 * The operator's settings: every configurable, on one page, saved per section.
 *
 * ## The page has no list of its own
 *
 * `GET /admin/settings` describes each setting — its label, help sentence,
 * kind, options, bounds, where its current value came from — and this screen
 * renders what it is told. A setting added to the Worker's registry appears
 * here with no web deploy, which is the point: "any configurable setting, in
 * the front end" is a promise the page can only keep if the Worker is the one
 * that says what is configurable.
 *
 * ## Drafts, dirt, and the guard
 *
 * The server's view is `data`; what the operator has typed and not saved is
 * `draft`, keyed by setting. A section's Save sends only its dirty keys, so a
 * half-edited Payouts section is not saved by pressing Save on Email — and
 * leaving the page with anything dirty asks first, through the router's
 * blocker for in-app navigation and `beforeunload` for the tab.
 *
 * ## Secrets are write-only
 *
 * A secret field never holds its current value. It shows a state — set and
 * its tail, set in the deploy, not set, or set but unreadable — and takes a
 * NEW value or a Clear. Nothing on this page can read a secret back, and
 * nothing in the API can either.
 */

type Draft = Record<string, string | null>;

const GROUP_NOTES: Record<SettingGroup, string> = {
  general: 'Where the system lives, and who may talk to it.',
  signup: 'Who gets in, and whether a person has to let them.',
  email: 'How creators receive their verification and reset links. Until a provider is on, links wait in the Outbox.',
  telegram: 'The bot that tells you about reviews, signups, payouts and support.',
  payouts: 'The floor, the weekly batch, and the accounts you pay from.',
  referrals: 'What a referrer earns of a friend’s earnings. Changes apply to bonuses accrued from now on; each bonus records its rate.',
  review: 'What creators are promised about review.',
  integrations: 'The clip library, Amplify’s export token, and the two keys only a deploy can set.',
};

/** The source chip's words. */
const SOURCE_LABEL: Record<SettingView['source'], string> = {
  settings: 'from settings',
  deploy: 'from deploy',
  default: 'default',
};

/** The draft representation of a server value: what the input holds. */
function draftOf(view: SettingView): string {
  if (view.secret) return '';
  if (view.kind === 'money') return centsToDollarInput(typeof view.value === 'number' ? view.value : null);
  if (view.kind === 'boolean') return view.value ? 'true' : 'false';
  return view.value === null ? '' : String(view.value);
}

/** What a draft means as a value for the PATCH body, or a refusal. */
function valueOf(view: SettingView, draft: string): { ok: true; value: string | number | boolean } | { ok: false; reason: string } {
  if (view.kind === 'money') {
    const parsed = parseDollars(draft);
    return parsed.ok ? { ok: true, value: parsed.cents } : { ok: false, reason: parsed.reason };
  }
  if (view.kind === 'boolean') return { ok: true, value: draft === 'true' };
  if (view.kind === 'integer' || (view.kind === 'select' && typeof view.default === 'number')) {
    const n = Number(draft.trim());
    if (draft.trim() === '' || !Number.isInteger(n)) return { ok: false, reason: 'A whole number.' };
    return { ok: true, value: n };
  }
  return { ok: true, value: draft };
}

function isWalletKey(key: string): boolean {
  return key.startsWith('USDT_SENDING_WALLET_');
}

function walletNetwork(key: string): CryptoNetwork | null {
  const network = key.slice('USDT_SENDING_WALLET_'.length);
  return (CRYPTO_NETWORKS as readonly string[]).includes(network) ? (network as CryptoNetwork) : null;
}

/** A value from the audit trail, as a phrase. */
function describeAudited(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    const v = value as { value?: unknown; source?: string };
    if ('value' in v) {
      const inner = describeAudited(v.value);
      return v.source && v.source !== 'settings' ? `${inner} (${v.source})` : inner;
    }
    const s = value as { set?: boolean; hint?: string | null };
    if (s.set) return s.hint ? `set (${s.hint})` : 'set';
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'string' && value === '') return '(empty)';
  return String(value);
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard?.writeText(text);
  } catch {
    /* A browser that refuses the clipboard: the text is still on the screen. */
  }
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function AdminSettings(): React.JSX.Element {
  const [data, setData] = useState<AdminSettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<SettingGroup | null>(null);
  const [notices, setNotices] = useState<Partial<Record<SettingGroup, { tone: 'positive' | 'critical'; text: string }>>>({});
  const [confirmWallets, setConfirmWallets] = useState<Array<{ key: string; label: string; last6: string }> | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await apiGet<AdminSettingsResponse>('/admin/settings');
      setData(body);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const byKey = useMemo(() => new Map((data?.settings ?? []).map((s) => [s.key, s])), [data]);

  /** Is this key's draft different from what the server holds? */
  const isDirty = useCallback(
    (key: string): boolean => {
      const view = byKey.get(key);
      if (!view || !(key in draft)) return false;
      const value = draft[key];
      if (value === null) return true; // a pending Clear
      if (value === undefined) return false;
      if (view.secret) return value.length > 0;
      return value !== draftOf(view);
    },
    [byKey, draft],
  );

  const dirtyKeys = useMemo(() => Object.keys(draft).filter(isDirty), [draft, isDirty]);
  const dirty = dirtyKeys.length > 0;

  /* THE GUARD. In-app navigation through the router's blocker; the tab
     through `beforeunload`, which is the only hook a browser offers. */
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const setValue = (key: string, value: string | null): void => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const discard = (group: SettingGroup): void => {
    setDraft((d) => {
      const next = { ...d };
      for (const view of data?.settings ?? []) if (view.group === group) delete next[view.key];
      return next;
    });
    setNotices((n) => ({ ...n, [group]: undefined }));
  };

  /** Build a section's PATCH body from its dirty keys; refuse locally what the server would. */
  const bodyFor = (group: SettingGroup): Record<string, unknown> | null => {
    const body: Record<string, unknown> = {};
    const errors: Record<string, string> = {};
    for (const key of dirtyKeys) {
      const view = byKey.get(key);
      if (!view || view.group !== group) continue;
      const value = draft[key];
      if (value === null) {
        body[key] = null;
        continue;
      }
      if (value === undefined) continue;
      if (isWalletKey(key)) {
        const network = walletNetwork(key);
        const verdict = network ? validateWalletAddress(network, value) : { ok: false as const, message: 'Unknown network.' };
        if (!verdict.ok) {
          errors[key] = verdict.message;
          continue;
        }
        body[key] = verdict.normalized;
        continue;
      }
      const parsed = valueOf(view, value);
      if (!parsed.ok) {
        errors[key] = parsed.reason;
        continue;
      }
      body[key] = parsed.value;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors((e) => ({ ...e, ...errors }));
      setNotices((n) => ({ ...n, [group]: { tone: 'critical', text: 'Fix the fields marked below.' } }));
      return null;
    }
    return body;
  };

  const save = async (group: SettingGroup, body: Record<string, unknown>): Promise<void> => {
    setBusy(group);
    setNotices((n) => ({ ...n, [group]: undefined }));
    try {
      const response = await apiFetch('/admin/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await response.json().catch(() => null)) as
        | (AdminSettingsResponse & { changed: string[] })
        | { error: { code: string; message: string; fields?: Record<string, string> } }
        | null;
      if (!response.ok || !json || 'error' in json) {
        const err = json && 'error' in json ? json.error : null;
        if (err?.fields) setFieldErrors((e) => ({ ...e, ...err.fields }));
        setNotices((n) => ({
          ...n,
          [group]: { tone: 'critical', text: err?.message ?? `Could not save (${response.status}).` },
        }));
        return;
      }
      setData(json);
      setDraft((d) => {
        const next = { ...d };
        for (const key of Object.keys(body)) delete next[key];
        return next;
      });
      const n = json.changed.length;
      setNotices((notice) => ({
        ...notice,
        [group]: {
          tone: 'positive',
          text: n === 0 ? 'Nothing had changed.' : `Saved ${n} setting${n === 1 ? '' : 's'}. In effect within thirty seconds everywhere.`,
        },
      }));
    } catch (err) {
      setNotices((n) => ({
        ...n,
        [group]: { tone: 'critical', text: err instanceof Error ? err.message : 'Could not save.' },
      }));
    } finally {
      setBusy(null);
    }
  };

  const onSave = (group: SettingGroup): void => {
    const body = bodyFor(group);
    if (!body) return;
    /* A sending wallet is where the operator's own money leaves from: it is
       confirmed with its tail before it is saved, exactly as a creator's is. */
    const wallets = Object.entries(body)
      .filter(([key, value]) => isWalletKey(key) && typeof value === 'string')
      .map(([key, value]) => ({
        key,
        label: byKey.get(key)?.label ?? key,
        last6: String(value).slice(-6),
      }));
    if (wallets.length > 0) {
      setConfirmWallets(wallets);
      return;
    }
    void save(group, body);
  };

  if (error && data === null) {
    return (
      <LoadErrorPage eyebrow="Operator" title="Settings" heading="Could not load the settings." onRetry={() => void load()}>
        <span>{error}</span>
      </LoadErrorPage>
    );
  }
  if (data === null) return <LoadingPage eyebrow="Operator" title="Settings" />;

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Settings"
        lead="Everything the system can be told from here, saved a section at a time. A value from the deploy stays until you change it."
        actions={
          dirty ? (
            <Badge tone="waiting">
              {dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? '' : 's'}
            </Badge>
          ) : undefined
        }
      />

      {!data.kekConfigured && (
        <Alert tone="critical" title="TOKEN_KEK is not set on this Worker.">
          <span>
            Secrets on this page — API keys, the bot token, wallets — are sealed with it, so none can be saved
            or read until it is set with <code className="chip quiet">wrangler secret put TOKEN_KEK</code> and
            the Worker is deployed. Ordinary settings still save.
          </span>
        </Alert>
      )}

      {SETTING_GROUPS.map((group) => (
        <Section
          key={group}
          group={group}
          data={data}
          draft={draft}
          fieldErrors={fieldErrors}
          busy={busy === group}
          notice={notices[group] ?? null}
          dirtyInGroup={dirtyKeys.filter((k) => byKey.get(k)?.group === group).length}
          onChange={setValue}
          onSave={() => onSave(group)}
          onDiscard={() => discard(group)}
        />
      ))}

      <AuditTrail changes={data.recentChanges} />

      {confirmWallets && (
        <Modal
          title="Change the sending wallet?"
          onClose={() => setConfirmWallets(null)}
          footer={
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWallets(null);
                const body = bodyFor('payouts');
                if (body) void save('payouts', body);
              }}
            >
              Save the wallet{confirmWallets.length === 1 ? '' : 's'}
            </Button>
          }
        >
          <p>
            USDT payouts on{' '}
            {confirmWallets.map((w, i) => (
              <span key={w.key}>
                {i > 0 ? ', ' : ''}
                <strong>{w.label.replace('USDT sending wallet — ', '')}</strong> will be sent from the wallet ending{' '}
                <code className="chip">…{w.last6}</code>
              </span>
            ))}
            . Check the tail against the wallet you hold before saving.
          </p>
        </Modal>
      )}

      {blocker.state === 'blocked' && (
        <Modal
          title="Leave without saving?"
          onClose={() => blocker.reset()}
          footer={
            <Button variant="danger" onClick={() => blocker.proceed()}>
              Leave and discard
            </Button>
          }
        >
          <p>
            {dirtyKeys.length} change{dirtyKeys.length === 1 ? ' has' : 's have'} not been saved. Leaving
            now throws {dirtyKeys.length === 1 ? 'it' : 'them'} away.
          </p>
        </Modal>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* One section                                                         */
/* ------------------------------------------------------------------ */

function Section({
  group,
  data,
  draft,
  fieldErrors,
  busy,
  notice,
  dirtyInGroup,
  onChange,
  onSave,
  onDiscard,
}: {
  group: SettingGroup;
  data: AdminSettingsResponse;
  draft: Draft;
  fieldErrors: Record<string, string>;
  busy: boolean;
  notice: { tone: 'positive' | 'critical'; text: string } | null;
  dirtyInGroup: number;
  onChange: (key: string, value: string | null) => void;
  onSave: () => void;
  onDiscard: () => void;
}): React.JSX.Element {
  const views = data.settings.filter((s) => s.group === group);
  return (
    <Card title={SETTING_GROUP_LABELS[group]} sub={GROUP_NOTES[group]} id={`settings-${group}`}>
      <form
        className="form"
        data-group={group}
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        {views.map((view) => (
          <SettingField
            key={view.key}
            view={view}
            draft={view.key in draft ? draft[view.key]! : draftOf(view)}
            pendingClear={draft[view.key] === null}
            error={fieldErrors[view.key] ?? null}
            kekConfigured={data.kekConfigured}
            onChange={(value) => onChange(view.key, value)}
          />
        ))}

        {group === 'integrations' && <DeployOnlyRows rows={data.deployOnly} />}

        <Actions>
          <Button type="submit" variant="primary" disabled={busy || dirtyInGroup === 0}>
            {busy ? 'Saving…' : dirtyInGroup === 0 ? 'Saved' : `Save ${SETTING_GROUP_LABELS[group].toLowerCase()}`}
          </Button>
          {dirtyInGroup > 0 && !busy && (
            <Button variant="quiet" onClick={onDiscard}>
              Discard changes
            </Button>
          )}
        </Actions>
        {notice && (
          <Alert tone={notice.tone}>
            <span data-testid={`notice-${group}`}>{notice.text}</span>
          </Alert>
        )}
      </form>

      {group === 'email' && <EmailTools data={data} draft={draft} />}
      {group === 'telegram' && <TestButton path="/admin/settings/test/telegram" label="Send a test Telegram message" />}
      {group === 'integrations' && <TestButton path="/admin/settings/test/library" label="Check the clip library" />}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* One field                                                           */
/* ------------------------------------------------------------------ */

function SourceChip({ view }: { view: SettingView }): React.JSX.Element {
  const title =
    view.source === 'deploy' && view.deployValue
      ? `The deploy sets ${view.deployValue}`
      : view.source === 'settings' && view.updatedAt
        ? `Saved ${view.updatedByEmail ? `by ${view.updatedByEmail} ` : ''}on ${new Date(view.updatedAt).toLocaleString()}`
        : view.source === 'default'
          ? 'Nothing set anywhere; the built-in default applies'
          : undefined;
  return (
    <span className="chip quiet" title={title} data-source={view.source}>
      {SOURCE_LABEL[view.source]}
    </span>
  );
}

function SettingField({
  view,
  draft,
  pendingClear,
  error,
  kekConfigured,
  onChange,
}: {
  view: SettingView;
  draft: string | null;
  pendingClear: boolean;
  error: string | null;
  kekConfigured: boolean;
  onChange: (value: string | null) => void;
}): React.JSX.Element {
  const hint = (
    <>
      {view.help} <SourceChip view={view} />
    </>
  );
  const name = view.key;

  if (view.secret) {
    return (
      <SecretField
        view={view}
        draft={draft ?? ''}
        pendingClear={pendingClear}
        error={error}
        kekConfigured={kekConfigured}
        onChange={onChange}
      />
    );
  }

  const value = draft ?? '';

  if (view.kind === 'boolean') {
    return (
      <SelectField label={view.label} hint={hint} error={error} name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="true">On</option>
        <option value="false">Off</option>
      </SelectField>
    );
  }

  if (view.kind === 'select') {
    return (
      <SelectField label={view.label} hint={hint} error={error} name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        {(view.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </SelectField>
    );
  }

  if (view.kind === 'money') {
    const parsed = parseDollars(value);
    return (
      <TextField
        label={view.label}
        hint={
          <>
            {hint}{' '}
            <span className={parsed.ok ? 'tone-positive' : undefined} data-cents-echo="true">
              {parsed.ok ? centsEcho(parsed.cents) : '—'}
            </span>
          </>
        }
        error={error}
        name={name}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="0.00"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (view.kind === 'integer') {
    return (
      <TextField
        label={view.label}
        hint={hint}
        error={error}
        name={name}
        type="number"
        inputMode="numeric"
        min={view.min}
        max={view.max}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <TextField
      label={view.label}
      hint={hint}
      error={error}
      name={name}
      type={view.kind === 'url' ? 'url' : 'text'}
      inputMode={view.kind === 'email' ? 'email' : undefined}
      autoComplete="off"
      spellCheck={false}
      value={value}
      placeholder={view.kind === 'timezone' ? 'America/New_York' : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function SecretField({
  view,
  draft,
  pendingClear,
  error,
  kekConfigured,
  onChange,
}: {
  view: SettingView;
  draft: string;
  pendingClear: boolean;
  error: string | null;
  kekConfigured: boolean;
  onChange: (value: string | null) => void;
}): React.JSX.Element {
  const state = view.secretState;
  const network = isWalletKey(view.key) ? walletNetwork(view.key) : null;
  const preview = network && draft.trim() ? validateWalletAddress(network, draft.trim()) : null;
  const multiline = view.key === 'WIRE_PAYOUT_NOTES';

  const status = pendingClear ? (
    <Badge tone="waiting">Will be cleared on save</Badge>
  ) : state?.unreadable ? (
    <Badge tone="critical">Set, but unreadable</Badge>
  ) : state?.set ? (
    <Badge tone="positive">
      Set{state.hint ? ` (${state.hint})` : ''}
      {view.source === 'deploy' ? ', from the deploy' : ''}
    </Badge>
  ) : (
    <Badge tone="neutral">Not set</Badge>
  );

  return (
    <Field
      label={view.label}
      error={error ?? (preview && !preview.ok ? preview.message : null)}
      hint={
        <>
          {view.help} <SourceChip view={view} />
          {state?.unreadable && !pendingClear && (
            <>
              {' '}
              <span className="tone-critical">
                The Worker cannot open what is stored (TOKEN_KEK changed?). Paste it again.
              </span>
            </>
          )}
          {preview?.ok && (
            <>
              {' '}
              <span className="tone-positive" data-testid={`wallet-preview-${view.key}`}>
                Will be saved as …{preview.last6}.
              </span>
            </>
          )}
        </>
      }
    >
      {(props) => (
        <div className="stack tight" data-secret={view.key}>
          <div className="actions" data-testid={`secret-state-${view.key}`}>
            {status}
            {view.source === 'settings' && state?.set && !pendingClear && (
              <Button small variant="quiet" onClick={() => onChange(null)}>
                Clear
              </Button>
            )}
            {pendingClear && (
              <Button small variant="quiet" onClick={() => onChange('')}>
                Keep it
              </Button>
            )}
          </div>
          {multiline ? (
            <textarea
              {...props}
              name={view.key}
              rows={4}
              value={pendingClear ? '' : draft}
              disabled={pendingClear || !kekConfigured}
              placeholder={state?.set ? 'Paste new notes to replace what is stored' : 'Bank name, account, reference format…'}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            <input
              {...props}
              type="password"
              name={view.key}
              autoComplete="new-password"
              spellCheck={false}
              value={pendingClear ? '' : draft}
              disabled={pendingClear || !kekConfigured}
              placeholder={state?.set ? 'Paste a new value to replace it' : 'Paste the value'}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </div>
      )}
    </Field>
  );
}

/* ------------------------------------------------------------------ */
/* Deploy-only rows                                                    */
/* ------------------------------------------------------------------ */

function DeployOnlyRows({ rows }: { rows: AdminSettingsResponse['deployOnly'] }): React.JSX.Element {
  return (
    <fieldset className="group">
      <legend>Set via deploy</legend>
      <p className="group-note">
        These two seal everything else and cannot be changed from a page: set them with{' '}
        <code className="chip quiet">wrangler secret put</code> and deploy.
      </p>
      {rows.map((row) => (
        <div className="field" key={row.key} data-deploy-only={row.key}>
          <label>{row.label}</label>
          <div className="actions">
            <Badge tone={row.set ? 'positive' : 'critical'}>{row.set ? 'Set' : 'Not set'}</Badge>
            <code className="chip quiet">{row.key}</code>
          </div>
          <p className="hint">{row.help}</p>
        </div>
      ))}
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* The test buttons                                                    */
/* ------------------------------------------------------------------ */

function TestButton({ path, label }: { path: string; label: string }): React.JSX.Element {
  const [result, setResult] = useState<SettingsTestResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await apiSend<SettingsTestResult>('POST', path));
    } catch (err) {
      setResult({ ok: false, message: err instanceof ApiError ? err.message : 'The test could not run.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack tight" data-test={path}>
      <Actions>
        <Button onClick={() => void run()} disabled={busy}>
          {busy ? 'Working…' : label}
        </Button>
      </Actions>
      {result && (
        <Alert tone={result.ok ? 'positive' : 'critical'}>
          <span>{result.message}</span>
        </Alert>
      )}
    </div>
  );
}

/**
 * The email section's two tools: a test send, and the domain check — which
 * shows Resend's own records when it can, and the records Resend always asks
 * for when it cannot (no key yet, the domain not added), each with a copy
 * button, so the operator can go straight to the DNS zone.
 */
function EmailTools({ data, draft }: { data: AdminSettingsResponse; draft: Draft }): React.JSX.Element {
  const [domain, setDomain] = useState<ResendDomainResult | null>(null);
  const [busy, setBusy] = useState(false);

  const fromView = data.settings.find((s) => s.key === 'EMAIL_FROM');
  const from = (draft.EMAIL_FROM ?? (fromView ? draftOf(fromView) : '')) || '';
  const match = /<([^>]+)>\s*$/.exec(from.trim());
  const address = (match ? match[1]! : from).trim();
  const sendingDomain = address.includes('@') ? address.slice(address.lastIndexOf('@') + 1).toLowerCase() : 'weclip.media';

  const check = async (): Promise<void> => {
    setBusy(true);
    try {
      setDomain(await apiSend<ResendDomainResult>('POST', '/admin/settings/test/resend-domain'));
    } catch (err) {
      setDomain({
        ok: false,
        message: err instanceof ApiError ? err.message : 'The check could not run.',
        domain: null,
        status: null,
        records: null,
      });
    } finally {
      setBusy(false);
    }
  };

  const expected = [
    { type: 'TXT', name: `resend._domainkey.${sendingDomain}`, value: 'p=… (the DKIM key Resend shows for the domain)' },
    { type: 'MX', name: `send.${sendingDomain}`, value: 'feedback-smtp.<region>.amazonses.com', priority: 10 },
    { type: 'TXT', name: `send.${sendingDomain}`, value: 'v=spf1 include:amazonses.com ~all' },
    { type: 'TXT', name: `_dmarc.${sendingDomain}`, value: 'v=DMARC1; p=none;' },
  ];
  const records = domain?.records && domain.records.length > 0 ? domain.records : null;

  return (
    <div className="stack" data-testid="email-tools">
      <Actions>
        <TestEmailButton to={data.operatorEmail} />
        <Button onClick={() => void check()} disabled={busy}>
          {busy ? 'Checking…' : 'Check the Resend domain'}
        </Button>
      </Actions>
      {domain && (
        <Alert tone={domain.ok ? 'positive' : 'critical'}>
          <span>{domain.message}</span>
        </Alert>
      )}
      <fieldset className="group">
        <legend>{records ? `DNS records Resend wants on ${domain?.domain}` : `DNS records Resend will ask for on ${sendingDomain}`}</legend>
        <p className="group-note">
          {records
            ? 'From Resend, live. Add each to the weclip.media zone in Cloudflare, then check again.'
            : 'The four records every Resend domain needs. Resend shows the exact DKIM value once the domain is added; the check above fetches the live set when a key is saved.'}
        </p>
        <DnsRecords rows={records ?? expected} />
      </fieldset>
    </div>
  );
}

function TestEmailButton({ to }: { to: string }): React.JSX.Element {
  const [result, setResult] = useState<SettingsTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (): Promise<void> => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await apiSend<SettingsTestResult>('POST', '/admin/settings/test/email'));
    } catch (err) {
      setResult({ ok: false, message: err instanceof ApiError ? err.message : 'The test could not run.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button onClick={() => void run()} disabled={busy} title={`Sends to ${to}`}>
        {busy ? 'Sending…' : `Send a test email to ${to}`}
      </Button>
      {result && (
        <Alert tone={result.ok ? 'positive' : 'critical'}>
          <span>{result.message}</span>
        </Alert>
      )}
    </>
  );
}

function DnsRecords({
  rows,
}: {
  rows: Array<{ type: string; name: string; value: string; priority?: number | null; status?: string | null }>;
}): React.JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);
  type Row = (typeof rows)[number];
  const columns: Array<Column<Row>> = [
    { key: 'type', header: 'Type', primary: true, cell: (r) => <code className="chip quiet">{r.type}</code> },
    {
      key: 'name',
      header: 'Name',
      cell: (r) => (
        <>
          <code className="chip break">{r.name}</code>
          <Button small variant="quiet" onClick={() => void copyText(r.name).then(() => setCopied(`${r.name}:name`))}>
            {copied === `${r.name}:name` ? 'Copied' : 'Copy'}
          </Button>
        </>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cell: (r) => (
        <>
          <code className="chip break">{r.value}</code>
          <Button small variant="quiet" onClick={() => void copyText(r.value).then(() => setCopied(`${r.name}:value`))}>
            {copied === `${r.name}:value` ? 'Copied' : 'Copy'}
          </Button>
          {r.priority ? <span className="cell-note">priority {r.priority}</span> : null}
          {r.status ? <span className="cell-note">{r.status}</span> : null}
        </>
      ),
    },
  ];
  return (
    <DataTable
      compact
      columns={columns}
      rows={rows}
      getKey={(r) => `${r.type}:${r.name}`}
      empty={<EmptyState title="No records to show.">Resend lists them once the domain is added.</EmptyState>}
    />
  );
}

/* ------------------------------------------------------------------ */
/* The audit trail                                                     */
/* ------------------------------------------------------------------ */

function AuditTrail({ changes }: { changes: AdminSettingsResponse['recentChanges'] }): React.JSX.Element {
  type Row = (typeof changes)[number];
  const columns: Array<Column<Row>> = [
    { key: 'when', header: 'When', primary: true, cell: (r) => <When value={r.createdAt} /> },
    { key: 'key', header: 'Setting', cell: (r) => <code className="chip quiet">{r.key}</code> },
    { key: 'who', header: 'By', cell: (r) => r.actorEmail ?? '—' },
    {
      key: 'change',
      header: 'Change',
      cell: (r) => (
        <span>
          {describeAudited(r.before)} → <strong>{describeAudited(r.after)}</strong>
        </span>
      ),
    },
  ];
  return (
    <Card title="Recent changes" sub="The last twenty saves from this page. Secrets appear as set or cleared, never as their value.">
      <DataTable
        compact
        columns={columns}
        rows={changes}
        getKey={(r) => r.id}
        empty={<EmptyState title="Nothing changed yet.">Every save from this page is recorded here, with who and when.</EmptyState>}
      />
    </Card>
  );
}
